import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@cloudscape-design/components/header';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Container from '@cloudscape-design/components/container';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Grid from '@cloudscape-design/components/grid';
import Button from '@cloudscape-design/components/button';
import Link from '@cloudscape-design/components/link';
import Icon from '@cloudscape-design/components/icon';
import ColumnLayout from '@cloudscape-design/components/column-layout';

/**
 * Business Acumen Program Landing Page
 *
 * Streamlined overview: Why it matters, How it works, Quick Start + Resources.
 */
export function BusinessAcumenPage(): React.ReactElement {
  const navigate = useNavigate();

  return (
    <ContentLayout
      data-testid="business-acumen-page"
      header={
        <Header
          variant="h1"
          description="A dynamic and immersive crash course for Solutions Architects to navigate complex business conversations"
          actions={
            <Button
              variant="primary"
              onClick={() => navigate('/programs/business-acumen/scenarios')}
            >
              Start a scenario
            </Button>
          }
        >
          SA Business Acumen Program
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Why This Matters — the hook */}
        <Container
          header={<Header variant="h2">Why This Matters</Header>}
        >
          <ColumnLayout columns={3} variant="text-grid">
            <SpaceBetween size="s">
              <Box variant="h3">Technology Alone Doesn't Win</Box>
              <Box variant="p">
                Customers buy outcomes — revenue growth, cost control, risk reduction.
                Your credibility grows when you tie AWS solutions directly to those levers.
              </Box>
            </SpaceBetween>
            <SpaceBetween size="s">
              <Box variant="h3">Speak Their Language</Box>
              <Box variant="p">
                A CFO wants lower run-rate costs. A CMO wants faster campaigns.
                Translate architecture into business impact and watch engagement change.
              </Box>
            </SpaceBetween>
            <SpaceBetween size="s">
              <Box variant="h3">Influence is Earned</Box>
              <Box variant="p">
                Executives say yes because they trust you understand their business.
              </Box>
            </SpaceBetween>
          </ColumnLayout>
        </Container>

        {/* How It Works — clear, visual, concise */}
        <Container
          header={<Header variant="h2">How It Works</Header>}
        >
          <ColumnLayout columns={3} variant="text-grid">
            <SpaceBetween size="s">
              <Box variant="h3">
                <Icon name="microphone" /> Voice Conversations
              </Box>
              <Box variant="p">
                Speak naturally with AI-powered executive personas in real-time
                voice conversations.
              </Box>
            </SpaceBetween>
            <SpaceBetween size="s">
              <Box variant="h3">
                <Icon name="gen-ai" /> Instant Evaluation
              </Box>
              <Box variant="p">
                Get scored across discovery, business acumen, communication, and
                solutioning with specific coaching to improve next time.
              </Box>
            </SpaceBetween>
            <SpaceBetween size="s">
              <Box variant="h3">
                <Icon name="folder" /> Real Industry Scenarios
              </Box>
              <Box variant="p">
                Practice across Financial Services, Healthcare, Technology, Manufacturing,
                and more, each with unique executive personas and business challenges.
              </Box>
            </SpaceBetween>
          </ColumnLayout>
        </Container>

        {/* Quick Start + Resources side by side */}
        <Grid
          gridDefinition={[
            { colspan: { default: 12, m: 4 } },
            { colspan: { default: 12, m: 4 } },
            { colspan: { default: 12, m: 4 } },
          ]}
        >
          <Container
            header={<Header variant="h2">Quick Start</Header>}
          >
            <SpaceBetween size="m">
              <SpaceBetween size="xs">
                <Box variant="p"><strong>1.</strong> Browse scenarios by industry</Box>
                <Box variant="p"><strong>2.</strong> Review the case study brief</Box>
                <Box variant="p"><strong>3.</strong> Have a voice conversation with the AI persona</Box>
                <Box variant="p"><strong>4.</strong> Review your scorecard and coaching feedback</Box>
              </SpaceBetween>
              <Button
                variant="primary"
                onClick={() => navigate('/programs/business-acumen/scenarios')}
              >
                Browse scenarios
              </Button>
            </SpaceBetween>
          </Container>

          <Container
            header={<Header variant="h2">Program Stats</Header>}
          >
            <Grid gridDefinition={[
              { colspan: 6 }, { colspan: 6 },
              { colspan: 6 }, { colspan: 6 },
            ]}>
              <SpaceBetween size="xs">
                <Box variant="h1" color="text-status-info">12+</Box>
                <Box variant="small">Cohorts</Box>
              </SpaceBetween>
              <SpaceBetween size="xs">
                <Box variant="h1" color="text-status-info">133+</Box>
                <Box variant="small">SAs trained</Box>
              </SpaceBetween>
              <SpaceBetween size="xs">
                <Box variant="h1" color="text-status-info">4.7</Box>
                <Box variant="small">Avg CSAT</Box>
              </SpaceBetween>
              <SpaceBetween size="xs">
                <Box variant="h1" color="text-status-info">3 yrs</Box>
                <Box variant="small">Running</Box>
              </SpaceBetween>
            </Grid>
          </Container>

          <Container
            header={<Header variant="h2">Resources</Header>}
          >
            <SpaceBetween size="s">
              <Link
                href="https://w.amazon.com/bin/view/AWS/AWS_SA_US-East/sa-biz-acumen/"
                external
                variant="primary"
              >
                Program Wiki
              </Link>
              <Link
                href="https://amazon.enterprise.slack.com/channels/sa-biz-acumen-program-attendees"
                external
              >
                Slack: #sa-biz-acumen-program-attendees
              </Link>
            </SpaceBetween>
          </Container>
        </Grid>
      </SpaceBetween>
    </ContentLayout>
  );
}

export default BusinessAcumenPage;
